import { Button } from "@teamscheme/button";
import { Redirect, useHistory, useLocation } from "react-router";
import { LocationDescriptorObject } from "history";
import { useCustomer } from "../../auth/CustomerProvider";
import CardSection, { TitleSection } from "../../formatting/CardSection";
import { FlowPage } from "../../formatting/Page";
import PageCard from "../../formatting/PageCard";
import FlowErrorPage from "../../shared/FlowErrorPage";
import FlowLoadingPage from "../../shared/FlowLoadingPage";
import { CustomerViewModel } from "../../shared/models/CustomerViewModel";
import { useContract } from "../ContractContext";
import { useContractUrls } from "../routing/ContractUrls";
import { MemberContractBenefitsList } from "./MemberContractPage";
import { ContractType } from "../model/ContractViewModel";
import LinkCrmInfoBox from "./LinkCrmInfoBox";
import { useMembershipContractClient } from "./MembershipContractClient";
import ErrorMessage from "../../shared/error/ErrorMessage";
import { ButtonLink } from "@teamscheme/button-link";

function MemberContractIngress({ customer }: { customer: CustomerViewModel }) {
  return (
    <div>
      <p>
        Du er logget inn med medlemsnummer {customer.customerId}. Nå har du
        tilgang til:
      </p>
      <MemberContractBenefitsList />
    </div>
  );
}

export function useMembershipUpgradePageLocation(): LocationDescriptorObject {
  const location = useLocation();
  const returnTo = location.pathname;
  return useMembershipUpgradePageLocationInner(returnTo);
}

export function useMembershipUpgradePageLocationInner(returnTo: string) {
  const url = useContractUrls().membershipUpgrade;
  const search = new URLSearchParams(url.search);
  search.set("returnTo", returnTo);
  const searchString = search.toString();
  return {
    ...url,
    search: searchString ? `?${searchString}` : "",
  };
}

export function useReturnLocation(
  queryParam: string = "returnTo"
): LocationDescriptorObject {
  const contract = useContract();
  const location = useLocation();
  const contractUrls = useContractUrls();
  const fallback = contractUrls.contract;
  const token =
    "buyerToken" in contract ? contract.buyerToken : contract.sellerToken;
  const returnTo = new URLSearchParams(location.search).get(queryParam);
  if (!returnTo) return fallback;
  return { pathname: returnTo, search: `?token=${token}` };
}

function MembershipUpgrade({
  customer,
  returnLocation,
}: {
  customer: CustomerViewModel;
  returnLocation: LocationDescriptorObject;
}) {
  const history = useHistory();
  const contract = useContract();
  const membershipUpgrade = useMembershipContractClient();

  if (
    membershipUpgrade.isActive ||
    contract.contractType?.current === ContractType.Member
  ) {
    return (
      <div>
        <MemberContractIngress customer={customer} />
        <ButtonLink variant="secondary" linkTo={returnLocation}>
          Tilbake til kontrakten
        </ButtonLink>
      </div>
    );
  }

  if (customer.qualifiesForMembershipUpgrade) {
    return (
      <div>
        <MemberContractIngress customer={customer} />
        <Button
          variant="primary"
          isLoading={membershipUpgrade.isLoading}
          onClick={async () => {
            await membershipUpgrade.activate();
            history.push(returnLocation);
          }}
        >
          Oppgrader kontrakten med medlemskap
        </Button>
        {membershipUpgrade.error ? (
          <ErrorMessage>En feil har opptsått! Prøv igjen.</ErrorMessage>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <LinkCrmInfoBox />
      <br />
      <ButtonLink variant="secondary" linkTo={returnLocation}>
        Tilbake til kontrakten
      </ButtonLink>
    </div>
  );
}

export default function MembershipUpgradeText() {
  const customer = useCustomer();
  const returnLocation = useReturnLocation();

  if (customer.isLoading) return <FlowLoadingPage />;
  if (customer.error) return <FlowErrorPage />;
  if (!customer.result) return <Redirect to={returnLocation} />;

  return (
    <FlowPage>
      <PageCard>
        <TitleSection title="Oppgrader med medlemskap"></TitleSection>
        <CardSection>
          <MembershipUpgrade
            customer={customer.result}
            returnLocation={returnLocation}
          />
        </CardSection>
      </PageCard>
    </FlowPage>
  );
}
